import type { UserProfileOptions } from '@leagueplatform/web-common';
import snakecaseKeys from 'snakecase-keys';
import {
  GET_USER_PROFILE_MESSAGE_TYPE,
  setUserProfile,
  UserProfile,
} from '@leagueplatform/user-profile-api';
import { useQueryClient, useMutation } from 'react-query';
import { useGetUserProfile } from '@leagueplatform/web-common';
import { usePreferredFirstName } from 'hooks/use-preferred-first-name.hook';

export const usePersonalInformation = (options?: UserProfileOptions) => {
  const { data, isLoading, error } = useGetUserProfile(options);
  const {
    data: firstNameData,
    isLoading: firstNameIsLoading,
    error: firstNameError,
  } = usePreferredFirstName();

  const fullName = `${firstNameData?.preferredFirstName?.toUpperCase()} ${
    data?.userProfile.lastName ?? ''
  }`;
  const userInitials = `${firstNameData?.preferredFirstName?.[0] ?? ''}${
    data?.userProfile?.lastName?.[0] ?? ''
  }`;

  const initialValues = {
    preferredFirstName: data?.userProfile?.preferredFirstName,
    dateOfBirth: data?.userProfile?.dateOfBirth,
    pronouns: data?.userProfile?.pronouns,
    genderIdentity: data?.userProfile?.genderIdentity,
  };

  return {
    data: { ...data, fullName, userInitials, initialValues },
    isLoading: isLoading || firstNameIsLoading,
    error: error ?? firstNameError,
  };
};

/**
 * @name useUpdateUserProfile
 * @desc React hook to update the user profile
 * @returns Function that updates user profile
 */
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const updateUserProfileMutation = useMutation(
    (userProfile: Partial<UserProfile>) => setUserProfile(userProfile),
    {
      onSuccess: () =>
        queryClient.invalidateQueries([GET_USER_PROFILE_MESSAGE_TYPE]),
    },
  );

  return async (userProfile: any) => {
    const serializedBody = snakecaseKeys(userProfile);
    await updateUserProfileMutation.mutateAsync(serializedBody);
  };
};
